import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Colors from '../../library/Colors';
import { surface, tone } from '../timeclock/tcFormat';
import {
    DEPARTMENTS, KINDS_LEGEND, addDays, departmentLabel, hours, isoDate, isoWeekday, kindColor, kindLabel,
    locale, mondayOf, niceDate, parseDate, rosterDay, schoolHolidayLabel, schoolHolidayOn, specialLabel, teamError, teamPost,
} from './teamFormat';
import { ClientField, StaffSelect } from './TeamParts';

const NAME_W = 150;
const SIZES = { week: { w: 104, h: 40 }, month: { w: 30, h: 30 }, year: { w: 11, h: 24 } };
const PAINT_KINDS = ['LEAVE', 'RECUP', 'SICK', 'SPECIAL', 'REPLACEMENT', 'INSTALL', 'CLEAR'];
const SPECIAL_CODES = ['KLEIN_VERLET', 'GEBOORTE', 'ADOPTIE', 'OUDERSCHAP', 'FAMILIAAL', 'ZORG', 'MANTELZORG', 'OPLEIDING', 'ONBETAALD', 'ANDERE'];

/**
 * Team board: one row per colleague, grouped by department, one column per
 * day, in a week, month or year view. Weekends and holidays are grey, days
 * off by roster hatched, home working ⌂, weekend duty a dot. Below
 * development and support the number present (Kristof counts for
 * development). Management can paint days directly.
 */
const TeamCalendarTab = ({ t, lang, theme, token, me, staff, reloadKey, failed, notify }) => {
    const today = me.today || isoDate(new Date());
    const [view, setView] = useState(() => {
        try { return window.sessionStorage.getItem('team_view') || 'month'; } catch (e) { return 'month'; }
    });
    const [anchor, setAnchor] = useState(today);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [again, setAgain] = useState(0);
    const [paint, setPaint] = useState(null);
    const [special, setSpecial] = useState('KLEIN_VERLET');
    const [remark, setRemark] = useState('');
    const [painting, setPainting] = useState(false);

    const changeView = (v) => {
        if (!v) return;
        try { window.sessionStorage.setItem('team_view', v); } catch (e) { /* private mode */ }
        setView(v);
    };

    const days = useMemo(() => {
        let from;
        let count;
        if (view === 'week') {
            from = mondayOf(anchor);
            count = 7;
        } else if (view === 'year') {
            from = anchor.substring(0, 4) + '-01-01';
            count = Math.round((parseDate(anchor.substring(0, 4) + '-12-31') - parseDate(from)) / 86400000) + 1;
        } else {
            from = anchor.substring(0, 7) + '-01';
            const d = parseDate(from);
            count = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        }
        const out = [];
        for (let i = 0; i < count; i++) out.push(addDays(from, i));
        return out;
    }, [view, anchor]);

    useEffect(() => {
        let alive = true;
        setBusy(true);
        teamPost('calendar', token, { from: days[0], to: days[days.length - 1] }).then((response) => {
            if (!alive) return;
            setBusy(false);
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(failed(response));
            }
        });
        return () => { alive = false; };
    }, [token, days, reloadKey, again, failed]);

    const shift = (delta) => {
        const d = parseDate(anchor);
        if (view === 'week') d.setDate(d.getDate() + 7 * delta);
        else if (view === 'year') d.setFullYear(d.getFullYear() + delta);
        else { d.setDate(1); d.setMonth(d.getMonth() + delta); }
        setAnchor(isoDate(d));
    };

    const index = useMemo(() => {
        const holidays = {};
        const entries = {};
        const home = {};
        const duty = {};
        (data?.holidays || []).forEach((h) => { holidays[h.day] = h; });
        (data?.entries || []).forEach((e) => { (entries[e.staff_id + '|' + e.day] = entries[e.staff_id + '|' + e.day] || []).push(e); });
        (data?.home || []).forEach((h) => { home[h.staff_id + '|' + h.day] = true; });
        (data?.duty || []).forEach((d) => { duty[d.staff_id + '|' + d.day] = true; });
        return { holidays, entries, home, duty };
    }, [data]);

    const s = surface(theme);
    const dark = theme === 'dark';
    const size = SIZES[view];
    const grey = dark ? 'rgba(255,255,255,0.06)' : '#EEF1F5';
    const hatch = 'repeating-linear-gradient(135deg, transparent 0 4px, ' + (dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)') + ' 4px 5px)';
    const dutyColor = dark ? '#9C7FE0' : '#7A56C2';
    // School holidays (Flemish Community): a very light green behind the day headers.
    const schoolColor = dark ? 'rgba(125,187,95,0.16)' : 'rgba(106,168,79,0.13)';
    // Today: SoftTouch-blue day header and a blue frame down the whole column (year view: a stronger tint).
    const blue = Colors.sft_blue;
    const todayFill = view === 'year' ? (dark ? 'rgba(0,113,187,0.6)' : 'rgba(0,113,187,0.35)') : (dark ? 'rgba(0,113,187,0.26)' : 'rgba(0,113,187,0.1)');
    // Department bars: SoftTouch green (#C0E4DA), lightened; in dark mode a faint green with green text.
    const groupFill = dark ? 'rgba(192,228,218,0.16)' : 'rgba(192,228,218,0.4)';
    const todayEdges = (day) => (day === today && view !== 'year' ? { borderLeft: '2px solid ' + blue, borderRight: '2px solid ' + blue } : {});
    const loc = locale(lang);
    const closed = (day) => isoWeekday(day) >= 6 || !!index.holidays[day];

    const title = view === 'year' ? anchor.substring(0, 4)
        : view === 'week' ? parseDate(days[0]).toLocaleDateString(loc, { day: 'numeric', month: 'short' }) + ' – '
            + parseDate(days[6]).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' })
            : parseDate(days[0]).toLocaleDateString(loc, { month: 'long', year: 'numeric' });

    // A ref, not only the state: a fast double click reaches here twice before the
    // re-render that would show `painting`, and must not send the same paint twice.
    const paintBusy = useRef(false);
    const sendPaint = (body, done, action = 'paint') => {
        paintBusy.current = true;
        setPainting(true);
        teamPost(action, token, body).then((response) => {
            paintBusy.current = false;
            setPainting(false);
            if (response?.success) setAgain((k) => k + 1);
            else notify(teamError(t, response), false);
            if (done) done(!!response?.success);
        });
    };
    // Installation: date and colleague come from the clicked cell; the dialog asks for the client and a remark
    // (prefilled when the day already has an installation, so it can be changed).
    const [install, setInstall] = useState(null);
    // A day that already has something: another colour (or Clear) first asks to overwrite, showing what is there;
    // the same colour opens that entry to change it, keeping its hours, remark, client and times.
    const [overwrite, setOverwrite] = useState(null);   // { person, day, list, clear }
    const [installList, setInstallList] = useState(null); // { person, day, entries }: a day with one or more installations
    const [confirmDelete, setConfirmDelete] = useState(false);
    const isWork = (e) => e.kind === 'INSTALL' || e.kind === 'INTERVENTION';
    const sortWork = (list) => [...list].sort((a, b) => (a.time_from || '99') < (b.time_from || '99') ? -1 : (a.time_from || '99') > (b.time_from || '99') ? 1 : 0);
    const [editing, setEditing] = useState(null);       // { person, day, kind, hours, maxHours, remark, special }
    const installFrom = (person, day, e, extra) => ({
        person, day, kind: e?.kind || 'INSTALL', editing: !!e, hours: e?.hours, staffId: person.id, dayTo: day, requestId: e?.request_id ?? null,
        remark: e?.remark || '', from: e?.time_from || '', to: e?.time_to || '',
        client: e?.lic_client_id ? { id: e.lic_client_id, name: e.client || '#' + e.lic_client_id } : (e?.client ? { id: null, name: e.client } : null),
        ...extra,
    });
    const openEditor = (person, day, e) => {
        if (e.kind === 'INSTALL') {
            setInstall(installFrom(person, day, e));
            return;
        }
        const rd = rosterDay(person.roster, day);
        setEditing({ person, day, kind: e.kind, hours: e.hours !== undefined && e.hours !== null ? String(e.hours) : '',
            maxHours: rd ? Number(rd.hours) : null, remark: e.remark || '', special: e.special_code || special });
    };
    const paintNew = (person, day) => {
        if (paint === 'INSTALL') {
            setInstall(installFrom(person, day, null));
            return;
        }
        if (paint === 'SPECIAL' && !remark.trim()) {
            notify(t('team_err_remark', 'A remark is required for special leave.'), false);
            return;
        }
        sendPaint({ staff_id: person.id, day, kind: paint, special_code: paint === 'SPECIAL' ? special : null, remark: paint === 'SPECIAL' ? remark : '' });
    };
    const paintDay = useCallback((person, day) => {
        if (!paint || painting || paintBusy.current) return;
        const list = index.entries[person.id + '|' + day] || [];
        if (paint === 'CLEAR') {
            if (list.length) setOverwrite({ person, day, list, clear: true });
            return;
        }
        // Installations: several per day are possible. A day with only installations shows them (open one or add one).
        if (paint === 'INSTALL' && list.length && list.every((e) => isWork(e) && e.status !== 'PENDING')) {
            setInstallList({ person, day, entries: sortWork(list) });
            return;
        }
        const same = list.filter((e) => e.kind === paint);
        if (same.length === 1 && list.length === 1 && same[0].status !== 'PENDING') {
            openEditor(person, day, same[0]);
            return;
        }
        if (list.length) {
            setOverwrite({ person, day, list, clear: false });
            return;
        }
        paintNew(person, day);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paint, painting, special, remark, token, index]);
    const confirmOverwrite = () => {
        const o = overwrite;
        setOverwrite(null);
        if (!o) return;
        if (o.clear) sendPaint({ staff_id: o.person.id, day: o.day, kind: 'CLEAR', special_code: null, remark: '' });
        else paintNew(o.person, o.day);
    };
    const editHoursBad = !!editing && editing.hours !== '' && (!(Number(editing.hours) > 0)
        || (editing.maxHours !== null && Number(editing.hours) > editing.maxHours + 0.001));
    const editRemarkBad = !!editing && editing.kind === 'SPECIAL' && !editing.remark.trim();
    const saveEdit = () => {
        if (!editing || paintBusy.current || editHoursBad || editRemarkBad) return;
        sendPaint({ staff_id: editing.person.id, day: editing.day, kind: editing.kind, hours: editing.hours === '' ? null : editing.hours,
            remark: editing.remark, special_code: editing.kind === 'SPECIAL' ? editing.special : null },
            (ok) => { if (ok) setEditing(null); }, 'editDay');
    };
    const entrySummary = (e) => [
        e.kind === 'SPECIAL' && e.special_code ? specialLabel(t, e.special_code) : kindLabel(t, e.kind),
        e.hours !== undefined && e.hours !== null ? hours(e.hours, lang) : '',
        e.time_from && e.time_to ? e.time_from + '–' + e.time_to : '',
        e.client || '',
        e.status === 'PENDING' ? t('team_status_pending', 'requested') : '',
    ].filter(Boolean).join(' · ');
    // Times are optional: both or neither, the end after the start ('HH:MM' strings compare in order).
    const installTimesBad = !!install && (!!install.from !== !!install.to || (install.from && install.to && install.to <= install.from));
    // Click on an installation/intervention outside paint mode: management may edit an installation,
    // everyone else (and interventions, which this dialog does not plan) only views it.
    const ownInstall = (person, e) => !me.rights.paint && person.id === me.staff.id && e.kind === 'INSTALL' && e.status === 'APPROVED' && !!e.request_id;
    const installRights = (person, e) => (me.rights.paint && e.kind === 'INSTALL' ? {} : ownInstall(person, e) ? { own: true } : { readOnly: true });
    const openInstall = (person, day, e) => {
        setInstall(installFrom(person, day, e, installRights(person, e)));
    };
    const openWorkDay = (person, day, list) => {
        const work = sortWork(list.filter(isWork));
        if (work.length === 1) openInstall(person, day, work[0]);
        else setInstallList({ person, day, entries: work });
    };
    // Remove only this installation (the other entries of the day stay); asked twice in the dialog.
    const deleteInstall = () => {
        if (!install?.editing || !install.requestId || paintBusy.current) return;
        sendPaint({ staff_id: install.person.id, day: install.day, request_id: install.requestId },
            (ok) => { if (ok) { setInstall(null); setConfirmDelete(false); } }, 'deleteEntry');
    };
    const saveInstall = () => {
        if (!install || paintBusy.current || installTimesBad) return;
        if (install.own) {
            // Own installation: only the times and the remark.
            sendPaint({ day: install.day, request_id: install.requestId, remark: install.remark,
                time_from: install.from || null, time_to: install.to || null }, (ok) => { if (ok) setInstall(null); }, 'editOwnInstall');
            return;
        }
        const body = { staff_id: install.person.id, day: install.day, kind: 'INSTALL', remark: install.remark,
            lic_client_id: install.client ? install.client.id : null, time_from: install.from || null, time_to: install.to || null };
        // An existing installation is changed in place (its hours stay), possibly moved to another colleague; a new one is painted.
        if (install.editing) {
            const moveTo = install.staffId && install.staffId !== install.person.id ? install.staffId : null;
            const moveDay = install.dayTo && install.dayTo !== install.day ? install.dayTo : null;
            sendPaint({ ...body, kind: install.kind || 'INSTALL', hours: install.hours ?? null, request_id: install.requestId,
                move_to_staff_id: moveTo, move_to_day: moveDay }, (ok) => { if (ok) setInstall(null); }, 'editDay');
        } else if (install.adding) {
            sendPaint(body, (ok) => { if (ok) setInstall(null); }, 'addInstall');
        } else sendPaint(body, (ok) => { if (ok) setInstall(null); });
    };

    const cellLabel = (e) => {
        if (e.kind === 'AWAY') return kindLabel(t, 'AWAY');
        if (e.kind === 'SPECIAL' && e.special_code) return specialLabel(t, e.special_code);
        if (e.kind === 'INSTALL' && (e.client || e.time_from)) return [e.time_from && e.time_to ? e.time_from + '–' + e.time_to : '', e.client || ''].filter(Boolean).join(' ');
        return kindLabel(t, e.kind);
    };

    const cell = (person, day) => {
        const weekend = isoWeekday(day) >= 6;
        const holiday = index.holidays[day];
        const list = index.entries[person.id + '|' + day] || [];
        const off = !weekend && !holiday && !rosterDay(person.roster, day);
        const home = index.home[person.id + '|' + day];
        const duty = index.duty[person.id + '|' + day];
        let background = weekend || holiday ? grey : off ? hatch : 'transparent';
        let border = 'none';
        let text = '';
        const tip = [];
        const when = parseDate(day).toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' });
        if (list.length) {
            const e = list.every(isWork) ? sortWork(list)[0] : list[0];
            const color = kindColor(e.kind, theme);
            const pending = e.status === 'PENDING';
            background = pending ? 'repeating-linear-gradient(135deg, ' + color + '55 0 4px, transparent 4px 8px)' : color;
            border = pending ? '1.5px solid ' + color : 'none';
            text = cellLabel(e) + (list.length > 1 && list.every(isWork) ? ' +' + (list.length - 1) : '');
            list.forEach((x) => {
                tip.push(cellLabel(x)
                    + (x.hours !== undefined && x.kind !== 'SPECIAL' ? ' · ' + hours(x.hours, lang) : '')
                    + (x.status === 'PENDING' ? ' · ' + t('team_status_pending', 'requested') : '')
                    + (x.source === 'MANAGEMENT' ? ' · ' + t('team_by_management', 'entered by management') : '')
                    + (x.remark ? ' · "' + x.remark.replace(/\s*\n\s*/g, ' / ') + '"' : ''));
            });
        } else if (holiday) {
            tip.push(holiday.name);
        } else if (off) {
            tip.push(t('team_legend_off', 'Day off by roster'));
        } else if (home && !weekend) {
            tip.push(t('team_home', 'Works from home'));
            text = '⌂ ' + t('team_home_short', 'home');
        }
        if (duty) {
            tip.push(t('team_weekend_duty', 'Weekend duty'));
            if (!text) text = t('team_weekend_duty', 'Weekend duty');
        }
        const paintable = paint && me.rights.paint && !weekend && !holiday && !off;
        const installEntry = list.find((x) => x.kind === 'INSTALL' || x.kind === 'INTERVENTION');
        const box = (
            <Box onClick={paintable ? () => paintDay(person, day) : installEntry ? () => openWorkDay(person, day, list) : undefined}
                sx={{ width: size.w, height: size.h, flexShrink: 0, borderLeft: view === 'year' ? 'none' : s.line, p: view === 'year' ? '2px 0.5px' : '3px',
                    boxSizing: 'border-box', position: 'relative', cursor: paintable ? 'crosshair' : installEntry ? 'pointer' : 'default',
                    borderLeftStyle: view === 'year' && day.endsWith('-01') ? 'solid' : undefined,
                    borderLeftWidth: view === 'year' && day.endsWith('-01') ? 1 : undefined,
                    borderLeftColor: view === 'year' && day.endsWith('-01') ? (dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') : undefined,
                    ...todayEdges(day),
                    backgroundColor: day === today ? todayFill : undefined,
                    '&:hover': paintable ? { outline: '2px solid ' + (paint === 'CLEAR' ? tone.muted : kindColor(paint, theme)), outlineOffset: -2 } : undefined }}>
                <Box sx={{ width: '100%', height: '100%', borderRadius: view === 'year' ? '1px' : '4px', background, border, boxSizing: 'border-box',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {view === 'week' && text && (
                        <Box component="span" sx={{ fontSize: 11.5, fontWeight: 600, px: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            color: list.length && list[0].status !== 'PENDING' ? '#fff' : 'text.secondary' }}>{text}</Box>
                    )}
                    {view === 'month' && !list.length && home && !weekend && (
                        <Box component="span" sx={{ fontSize: 13, color: s.faint, lineHeight: 1 }}>⌂</Box>
                    )}
                </Box>
                {duty && view !== 'week' && (
                    <Box sx={{ position: 'absolute', width: view === 'year' ? 5 : 8, height: view === 'year' ? 5 : 8, borderRadius: '50%', backgroundColor: dutyColor,
                        top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                )}
            </Box>
        );
        if (!tip.length || view === 'year' && !list.length && !duty) return <React.Fragment key={day}>{box}</React.Fragment>;
        return (
            <Tooltip key={day} disableInteractive title={
                <Box sx={{ fontSize: 12.5, lineHeight: 1.5 }}><b>{person.name}</b> · {when}{tip.map((x, i) => <div key={i}>{x}</div>)}</Box>
            }>{box}</Tooltip>
        );
    };

    // Occupancy rows: development at least 1 present (Kristof counts), support at most 2 away.
    const coverRow = (kind) => {
        const members = staff.filter((p) => p.coverage === kind);
        if (!members.length) return null;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', borderTop: s.line, backgroundColor: dark ? 'rgba(255,255,255,0.02)' : '#FAFBFC' }}>
                <Box sx={{ width: NAME_W, flexShrink: 0, px: 1.5, fontSize: 12, color: s.faint, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {kind === 'DEV' ? t('team_cover_dev', 'Dev present') : t('team_cover_support', 'Support present')}
                </Box>
                {days.map((day) => {
                    if (closed(day)) return <Box key={day} sx={{ width: size.w, flexShrink: 0, height: 22, borderLeft: view === 'year' ? 'none' : s.line, boxSizing: 'border-box', ...todayEdges(day) }} />;
                    const working = members.filter((p) => rosterDay(p.roster, day));
                    const away = working.filter((p) => (index.entries[p.id + '|' + day] || []).length > 0).length;
                    const present = working.length - away;
                    const color = kind === 'DEV'
                        ? (present < 1 ? tone.bad : tone.ok)
                        : (away > 2 ? tone.bad : away === 2 ? tone.warn : tone.ok);
                    return (
                        <Tooltip key={day} disableInteractive title={present + ' ' + t('team_present', 'present') + ', ' + away + ' ' + t('team_away_count', 'away')}>
                            <Box sx={{ width: size.w, flexShrink: 0, height: 22, borderLeft: view === 'year' ? 'none' : s.line, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 11.5, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', ...todayEdges(day),
                                backgroundColor: view === 'year' ? color + '33' : day === today ? todayFill : undefined }}>
                                {view === 'year' ? '' : present}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        );
    };

    const groups = DEPARTMENTS.map((dept) => ({ dept, people: staff.filter((p) => p.department === dept) })).filter((g) => g.people.length);

    return (
        <Box>
            <Dialog open={!!install} onClose={painting ? undefined : () => { setInstall(null); setConfirmDelete(false); }} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography component="span" sx={{ display: 'block', fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
                            {install?.readOnly ? kindLabel(t, install.kind || 'INSTALL') : install?.editing ? t('team_install_edit', 'Change installation') : install?.adding ? t('team_install_add', 'Add an installation') : t('team_install_dialog', 'Plan an installation')}
                        </Typography>
                        {install && (
                            <Typography component="span" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                                {install.person.name} · {niceDate(install.day, lang)}
                            </Typography>
                        )}
                    </Box>
                    {install?.editing && !install.readOnly && me.rights.paint && (
                        // One more installation for the same colleague and day (empty form; changes not saved here are dropped).
                        <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={painting} sx={{ textTransform: 'none', flexShrink: 0, mt: 0.25 }}
                            onClick={() => { setConfirmDelete(false); setInstall(installFrom(install.person, install.day, null, { adding: true })); }}>
                            {t('team_install_new', 'New installation')}
                        </Button>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2.5 }}>
                    {install?.readOnly && (
                        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 1.25, fontSize: 14 }}>
                            <Box component="dt" sx={{ color: 'text.secondary' }}>{t('team_client_short', 'Client')}</Box>
                            <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>{install.client?.name || '—'}</Box>
                            <Box component="dt" sx={{ color: 'text.secondary' }}>{t('team_install_time', 'Time')}</Box>
                            <Box component="dd" sx={{ m: 0, fontVariantNumeric: 'tabular-nums' }}>
                                {install.from && install.to ? install.from + ' – ' + install.to : t('team_install_whole_day', 'Whole day')}
                            </Box>
                            <Box component="dt" sx={{ color: 'text.secondary' }}>{t('team_remark', 'Remark')}</Box>
                            <Box component="dd" sx={{ m: 0, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{install.remark || '—'}</Box>
                        </Box>
                    )}
                    {install && !install.readOnly && (
                        <>
                            {install.own && (
                                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 1, fontSize: 14 }}>
                                    <Box component="dt" sx={{ color: 'text.secondary' }}>{t('team_client_short', 'Client')}</Box>
                                    <Box component="dd" sx={{ m: 0, fontWeight: 600 }}>{install.client?.name || '—'}</Box>
                                </Box>
                            )}
                            {install.editing && !install.own && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 160px' }, gap: 1.5 }}>
                                    <StaffSelect staff={staff} value={install.staffId} t={t} label={t('team_install_colleague', 'Colleague')}
                                        filter={(p) => p.id === install.person.id || !!rosterDay(p.roster, install.dayTo || install.day)}
                                        onChange={(v) => setInstall((x) => ({ ...x, staffId: v }))} sx={{ width: '100%', minWidth: 0 }} />
                                    <TextField size="small" type="date" label={t('team_install_date', 'Date')} value={install.dayTo || install.day}
                                        onChange={(e) => { const v = e.target.value; setInstall((x) => ({ ...x, dayTo: v || x.day })); }}
                                        slotProps={{ inputLabel: { shrink: true } }} />
                                </Box>
                            )}
                            {!install.own && <ClientField t={t} token={token} autoFocus value={install.client} onChange={(v) => setInstall((x) => ({ ...x, client: v }))} />}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                                <TextField size="small" type="time" label={t('team_time_from', 'From')} value={install.from}
                                    onChange={(e) => { const v = e.target.value; setInstall((x) => ({ ...x, from: v })); }}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300 } }} />
                                <TextField size="small" type="time" label={t('team_time_to', 'Until')} value={install.to}
                                    onChange={(e) => { const v = e.target.value; setInstall((x) => ({ ...x, to: v })); }}
                                    error={installTimesBad} helperText={installTimesBad ? t('team_err_times', 'Fill in both times, the end after the start.') : ' '}
                                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 300 } }} />
                            </Box>
                            <TextField size="small" multiline minRows={3} maxRows={8} label={t('team_remark', 'Remark')} value={install.remark}
                                onChange={(e) => { const v = e.target.value; setInstall((x) => ({ ...x, remark: v })); }} inputProps={{ maxLength: 500 }} />
                            {install.own && (
                                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                                    {t('team_install_own_note', 'You can change the times and the remark of your own installation; the client, the day and the colleague are planned by management.')}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', display: install.own ? 'none' : undefined }}>
                                {t('team_install_dialog_note', 'Times are optional: without times the installation takes the whole day. The client, times and remark appear in the calendar and in the Discord message of that day and the day before; Google Calendar shows the client and the times.')}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    {install?.readOnly ? (
                        <Button variant="contained" disableElevation onClick={() => setInstall(null)} sx={{ textTransform: 'none' }}>{t('close', 'Close')}</Button>
                    ) : (
                        <>
                            {install?.editing && install.requestId && !install.own && (
                                <Button color="error" disabled={painting} onClick={() => (confirmDelete ? deleteInstall() : setConfirmDelete(true))}
                                    variant={confirmDelete ? 'contained' : 'text'} disableElevation sx={{ textTransform: 'none', mr: 'auto' }}>
                                    {confirmDelete ? t('team_delete_sure', 'Really delete?') : t('team_delete', 'Delete')}
                                </Button>
                            )}
                            <Button onClick={() => { setInstall(null); setConfirmDelete(false); }} disabled={painting} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                            <Button variant="contained" disableElevation onClick={saveInstall} disabled={painting || installTimesBad}
                                startIcon={painting ? <CircularProgress size={14} color="inherit" /> : null} sx={{ textTransform: 'none' }}>
                                {install?.own ? t('team_save', 'Save') : t('team_plan', 'Plan')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={!!installList} onClose={() => setInstallList(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Typography component="span" sx={{ display: 'block', fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
                        {t('team_install_list', 'Installations on this day')}
                    </Typography>
                    {installList && (
                        <Typography component="span" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                            {installList.person.name} · {niceDate(installList.day, lang)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2 }}>
                    {(installList?.entries || []).map((e, i) => {
                        const rights = installRights(installList.person, e);
                        const canEdit = !rights.readOnly;
                        return (
                            <Box key={e.request_id || i} sx={{ display: 'flex', gap: 1.25, alignItems: 'center', p: 1.25, borderRadius: '8px', border: s.line }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '3px', flexShrink: 0, backgroundColor: kindColor(e.kind, theme) }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                        {[e.time_from && e.time_to ? e.time_from + '–' + e.time_to : '', e.client || kindLabel(t, e.kind)].filter(Boolean).join(' · ')}
                                    </Typography>
                                    {e.remark && (
                                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {e.remark.replace(/\s*\n\s*/g, ' / ')}
                                        </Typography>
                                    )}
                                </Box>
                                <Button size="small" variant="outlined" sx={{ textTransform: 'none', flexShrink: 0 }}
                                    onClick={() => { const l = installList; setInstallList(null); setInstall(installFrom(l.person, l.day, e, rights)); }}>
                                    {canEdit ? t('team_change', 'Change') : t('team_view', 'View')}
                                </Button>
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={() => setInstallList(null)} sx={{ textTransform: 'none' }}>{t('close', 'Close')}</Button>
                    {me.rights.paint && (
                        <Button variant="contained" disableElevation sx={{ textTransform: 'none' }}
                            onClick={() => { const l = installList; setInstallList(null); setInstall(installFrom(l.person, l.day, null, { adding: true })); }}>
                            {t('team_install_add', 'Add an installation')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            <Dialog open={!!overwrite} onClose={() => setOverwrite(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Typography component="span" sx={{ display: 'block', fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
                        {overwrite?.clear ? t('team_clear_title', 'Clear this day?') : t('team_overwrite_title', 'Overwrite this day?')}
                    </Typography>
                    {overwrite && (
                        <Typography component="span" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                            {overwrite.person.name} · {niceDate(overwrite.day, lang)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 2 }}>
                    <Typography sx={{ fontSize: 14 }}>
                        {overwrite?.clear
                            ? t('team_clear_text', 'This day now has the following; it will be removed:')
                            : t('team_overwrite_text', 'This day already has the following. It will be replaced by {{kind}}:', { kind: kindLabel(t, paint || 'LEAVE') })}
                    </Typography>
                    {(overwrite?.list || []).map((e, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', p: 1.25, borderRadius: '8px', border: s.line }}>
                            <Box sx={{ width: 12, height: 12, mt: 0.5, borderRadius: '3px', flexShrink: 0, backgroundColor: kindColor(e.kind, theme) }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{entrySummary(e)}</Typography>
                                {e.remark && <Typography sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{e.remark}</Typography>}
                            </Box>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={() => setOverwrite(null)} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button variant="contained" color="warning" disableElevation onClick={confirmOverwrite} sx={{ textTransform: 'none' }}>
                        {overwrite?.clear ? t('team_clear_btn', 'Clear') : t('team_overwrite_btn', 'Overwrite')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={!!editing} onClose={painting ? undefined : () => setEditing(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Typography component="span" sx={{ display: 'block', fontSize: 18, fontWeight: 700, lineHeight: 1.3 }}>
                        {t('team_edit_day', 'Change {{kind}}', { kind: editing ? kindLabel(t, editing.kind).toLowerCase() : '' })}
                    </Typography>
                    {editing && (
                        <Typography component="span" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                            {editing.person.name} · {niceDate(editing.day, lang)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2.5 }}>
                    {editing && (
                        <>
                            {editing.kind === 'SPECIAL' && (
                                <TextField select size="small" label={t('team_special_kind', 'Kind of special leave')} value={editing.special}
                                    onChange={(e) => { const v = e.target.value; setEditing((x) => ({ ...x, special: v })); }}>
                                    {SPECIAL_CODES.map((c) => <MenuItem key={c} value={c}>{specialLabel(t, c)}</MenuItem>)}
                                </TextField>
                            )}
                            <TextField size="small" type="number" label={t('team_hours', 'Hours')} value={editing.hours}
                                onChange={(e) => { const v = e.target.value; setEditing((x) => ({ ...x, hours: v })); }}
                                error={editHoursBad}
                                helperText={editHoursBad ? t('team_err_hours', 'Hours: for one day, more than 0 and at most the roster hours.')
                                    : editing.maxHours !== null ? t('team_edit_hours_help', 'Whole day = {{hours}}', { hours: hours(editing.maxHours, lang) }) : ' '}
                                slotProps={{ htmlInput: { min: 0.25, step: 0.25, max: editing.maxHours ?? undefined } }} />
                            <TextField size="small" multiline minRows={3} maxRows={8} label={t('team_remark', 'Remark')} value={editing.remark}
                                error={editRemarkBad} helperText={editRemarkBad ? t('team_err_remark', 'A remark is required for special leave.') : ' '}
                                onChange={(e) => { const v = e.target.value; setEditing((x) => ({ ...x, remark: v })); }} inputProps={{ maxLength: 500 }} />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={() => setEditing(null)} disabled={painting} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button variant="contained" disableElevation onClick={saveEdit} disabled={painting || editHoursBad || editRemarkBad}
                        startIcon={painting ? <CircularProgress size={14} color="inherit" /> : null} sx={{ textTransform: 'none' }}>
                        {t('save', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <IconButton size="small" onClick={() => shift(-1)} aria-label={t('team_prev', 'Previous')}><ChevronLeft /></IconButton>
                <Typography sx={{ fontSize: 16, fontWeight: 700, minWidth: 170, textAlign: 'center', textTransform: 'capitalize' }}>{title}</Typography>
                <IconButton size="small" onClick={() => shift(1)} aria-label={t('team_next', 'Next')}><ChevronRight /></IconButton>
                {!days.includes(today) && (
                    <Button size="small" onClick={() => setAnchor(today)} sx={{ textTransform: 'none' }}>{t('team_today', 'Today')}</Button>
                )}
                {(busy || painting) && <CircularProgress size={16} sx={{ ml: 1 }} />}
                <Box sx={{ flex: 1 }} />
                <ToggleButtonGroup size="small" exclusive value={view} onChange={(e, v) => changeView(v)}>
                    <ToggleButton value="week" sx={{ textTransform: 'none', px: 1.5 }}>{t('team_view_week', 'Week')}</ToggleButton>
                    <ToggleButton value="month" sx={{ textTransform: 'none', px: 1.5 }}>{t('team_view_month', 'Month')}</ToggleButton>
                    <ToggleButton value="year" sx={{ textTransform: 'none', px: 1.5 }}>{t('team_view_year', 'Year')}</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {me.rights.paint && (
                <Box sx={{ ...s.card, p: 1.25, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: s.faint, mr: 0.5 }}>{t('team_paint', 'Fill in the calendar:')}</Typography>
                    {PAINT_KINDS.map((k) => {
                        const on = paint === k;
                        return (
                            <Button key={k} size="small" variant={on ? 'contained' : 'outlined'} disableElevation onClick={() => setPaint(on ? null : k)}
                                sx={{ textTransform: 'none', height: 30, px: 1.25, fontSize: 12.5,
                                    ...(on ? { backgroundColor: k === 'CLEAR' ? tone.muted : kindColor(k, theme), color: k === 'REPLACEMENT' ? '#1a1a1a' : '#fff',
                                        '&:hover': { backgroundColor: k === 'CLEAR' ? tone.muted : kindColor(k, theme) } } : { color: 'text.primary', borderColor: s.ghost }) }}
                                startIcon={k === 'CLEAR' ? null : <Box component="span" sx={{ width: 10, height: 10, borderRadius: '3px', backgroundColor: kindColor(k, theme), border: on ? '1px solid #fff' : 'none' }} />}>
                                {k === 'CLEAR' ? t('team_paint_clear', 'Clear') : kindLabel(t, k)}
                            </Button>
                        );
                    })}
                    {paint === 'SPECIAL' && (
                        <>
                            <TextField select size="small" value={special} onChange={(e) => setSpecial(e.target.value)} sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 32, fontSize: 13 } }}>
                                {SPECIAL_CODES.map((c) => <MenuItem key={c} value={c}>{specialLabel(t, c)}</MenuItem>)}
                            </TextField>
                            <TextField size="small" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder={t('team_remark_required', 'Remark (required)')}
                                sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 32, fontSize: 13 } }} />
                        </>
                    )}
                    {paint && (
                        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ml: 0.5 }}>
                            {paint === 'CLEAR' ? t('team_paint_hint_clear', 'Tap the days that must be empty.') : t('team_paint_hint', 'Tap the days that get this colour; approved at once.')}
                        </Typography>
                    )}
                </Box>
            )}

            {error && <Typography sx={{ color: tone.bad, fontSize: 13.5, mb: 1 }}>{error}</Typography>}

            <Box sx={{ ...s.card, overflowX: 'auto' }}>
                <Box sx={{ minWidth: NAME_W + size.w * days.length }}>
                    <Box sx={{ display: 'flex', borderBottom: s.line }}>
                        <Box sx={{ width: NAME_W, flexShrink: 0 }} />
                        {days.map((day) => {
                            const d = parseDate(day);
                            const muted = closed(day);
                            const color = day === today ? '#fff' : muted ? s.faint : 'text.primary';
                            if (view === 'year') {
                                return (
                                    <Box key={day} sx={{ width: size.w, flexShrink: 0, position: 'relative', height: 30 }}>
                                        {d.getDate() === 1 && (
                                            <Box sx={{ position: 'absolute', left: 2, top: 7, fontSize: 11.5, fontWeight: 700, color: s.faint, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                                                {d.toLocaleDateString(loc, { month: 'short' })}
                                            </Box>
                                        )}
                                        {day === today && (
                                            <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 7, backgroundColor: blue, borderRadius: '2px 2px 0 0' }} />
                                        )}
                                    </Box>
                                );
                            }
                            const school = schoolHolidayOn(day);
                            const head = (
                                <Box key={day} sx={{ width: size.w, flexShrink: 0, textAlign: 'center', py: 0.5, borderLeft: s.line, boxSizing: 'border-box', color, fontWeight: day === today ? 800 : 500,
                                    ...todayEdges(day), backgroundColor: day === today ? blue : school ? schoolColor : undefined, borderRadius: day === today ? '6px 6px 0 0' : undefined }}>
                                    <Box sx={{ fontSize: 10.5, lineHeight: 1.2, textTransform: 'uppercase' }}>
                                        {d.toLocaleDateString(loc, { weekday: view === 'week' ? 'short' : 'narrow' })}
                                    </Box>
                                    <Box sx={{ fontSize: 12.5, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                                        {view === 'week' ? d.toLocaleDateString(loc, { day: 'numeric', month: 'short' }) : d.getDate()}
                                    </Box>
                                </Box>
                            );
                            return school
                                ? <Tooltip key={day} disableInteractive title={schoolHolidayLabel(t, school)}>{head}</Tooltip>
                                : head;
                        })}
                    </Box>
                    {groups.map((g) => (
                        <React.Fragment key={g.dept}>
                            <Box sx={{ px: 1.5, py: 0.6, fontSize: 11.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: dark ? Colors.sft_blue_text : Colors.sft_blue_darker, backgroundColor: groupFill }}>
                                {departmentLabel(t, g.dept)}
                            </Box>
                            {g.people.map((p) => (
                                <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', borderTop: s.line }}>
                                    <Box sx={{ width: NAME_W, flexShrink: 0, px: 1.5, fontSize: 13.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        fontWeight: p.id === me.staff.id ? 700 : 400 }}>
                                        {p.name}
                                        {p.coverage === 'DEV' && p.department !== 'DEV' && <Box component="span" sx={{ color: s.faint, fontSize: 11.5 }}> · dev</Box>}
                                    </Box>
                                    {days.map((day) => cell(p, day))}
                                </Box>
                            ))}
                            {g.dept === 'DEV' && coverRow('DEV')}
                            {g.dept === 'SUPPORT' && coverRow('SUPPORT')}
                        </React.Fragment>
                    ))}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 1, mt: 1.5, fontSize: 12.5, color: 'text.secondary', alignItems: 'center' }}>
                {KINDS_LEGEND.filter((k) => k !== 'AWAY' || !me.rights.sees_all).map((k) => (
                    <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: kindColor(k, theme) }} />
                        {k === 'AWAY' ? t('team_legend_away', 'Away (colleague)') : kindLabel(t, k)}
                    </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', border: '1.5px solid ' + kindColor('LEAVE', theme), background: 'repeating-linear-gradient(135deg, ' + kindColor('LEAVE', theme) + '55 0 3px, transparent 3px 6px)', boxSizing: 'border-box' }} />
                    {t('team_legend_pending', 'Requested')}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dutyColor }} /></Box>
                    {t('team_weekend_duty', 'Weekend duty')}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box component="span" sx={{ width: 14, textAlign: 'center' }}>⌂</Box>
                    {t('team_home', 'Works from home')}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: grey }} />
                    {t('team_legend_closed', 'Weekend / holiday')}
                </Box>
                {view !== 'year' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 14, height: 14, borderRadius: '3px', backgroundColor: schoolColor, border: s.line, boxSizing: 'border-box' }} />
                        {t('team_legend_school', 'School holidays (Flanders)')}
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', background: hatch, border: s.line, boxSizing: 'border-box' }} />
                    {t('team_legend_off', 'Day off by roster')}
                </Box>
            </Box>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1 }}>
                {me.rights.sees_all
                    ? t('team_vis_all', 'Management and Els see every colour, sickness and special leave included.')
                    : t('team_vis_staff', 'You see your own row in detail; of colleagues you only see that they are away, in one colour.')}
            </Typography>
        </Box>
    );
};

export default TeamCalendarTab;
